<script>
  import { format } from 'svelte-i18n';
  import { useNavigate } from 'svelte-navigator';
  import { Button, Input, Modal, ModalBody, ModalFooter } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';
  import { getNotificationsContext } from '@client/components/notifications';
  import { Select } from '@client/components/select';
  import { Spinner } from '@client/components/spinner';

  import { createWorkflow, updateWorkflow, workflowsStore } from '@client/stores/workflows';

  import { sanitizeRulenameOnKeyUp } from '@client/utils/sanitize';

  import { getAllWorkflowActions } from '../../workflow-steps-config';

  const { addNotification } = getNotificationsContext();
  const navigate = useNavigate();

  export let show = false;
  export let workflow = null; // If provided, we're editing; otherwise creating

  let updating = false;
  let error = '';
  let nameError = '';

  let workflowName = '';
  let steps = [
    {
      action: '',
      data: {}
    }
  ];
  let hovering = null;
  let startIndex = null;
  let initialized = false;

  const actionItems = getAllWorkflowActions().map((action) => ({
    ...action,
    value: action.value,
    label: action.subItemLabel
      ? `${action.categoryLabel} → ${action.itemLabel} → ${action.subItemLabel} → ${action.label}`
      : `${action.categoryLabel} → ${action.itemLabel} → ${action.label}`,
    description: action.description,
    category: action.category,
    categoryLabel: action.categoryLabel,
    item: action.item,
    itemLabel: action.itemLabel,
    subItem: action.subItem,
    subItemLabel: action.subItemLabel,
    searchWords: action.searchWords || []
  }));

  const actionItemFilter = (label, filterText, option) => {
    const searchText = filterText.toLowerCase().trim();

    if (!searchText) {
      return true;
    }

    // Split search text into individual words
    const searchTerms = searchText.split(/\s+/);

    // Search in label - check if all search terms are present
    const labelLower = label.toLowerCase();
    if (searchTerms.every((term) => labelLower.includes(term))) {
      return true;
    }

    // Search in description - check if all search terms are present
    const descriptionLower = option?.description?.toLowerCase() || '';
    if (searchTerms.every((term) => descriptionLower.includes(term))) {
      return true;
    }

    // Search in searchWords array - check if all search terms match at least one searchWord
    const searchWords = option?.searchWords || [];
    const searchWordsLower = searchWords.map((word) => word.toLowerCase());

    if (searchTerms.every((term) => searchWordsLower.some((word) => word.includes(term)))) {
      return true;
    }

    return false;
  };

  const resetModal = () => {
    show = false;
    workflow = null;
    workflowName = '';
    nameError = '';
    steps = [
      {
        action: '',
        data: {}
      }
    ];
  };

  const onNameKeyUp = () => {
    workflowName = sanitizeRulenameOnKeyUp(workflowName);
    validateWorkflowName();
  };

  const validateWorkflowName = () => {
    if (!workflowName) {
      nameError = '';
      return;
    }

    if (workflowName.length < 3) {
      nameError = 'Workflow name must be at least 3 characters';
      return;
    }

    if (!/^[a-z]/i.test(workflowName)) {
      nameError = 'Workflow name must start with a letter';
      return;
    }

    const exists = $workflowsStore.some((w) => {
      if (isEditMode && w.name === workflow.name) {
        return false;
      }
      return w.name.toLowerCase() === workflowName.toLowerCase();
    });

    if (exists) {
      nameError = 'A workflow with this name already exists';
    } else {
      nameError = '';
    }
  };

  const onAddStep = () => {
    steps.push({
      action: '',
      data: {}
    });
    steps = [...steps];
  };

  const removeStep = (index) => {
    steps.splice(index, 1);
    steps = [...steps];
  };

  const dragstart = (event, index) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.dropEffect = 'move';
    startIndex = index;
  };

  const drop = (event, index) => {
    event.dataTransfer.dropEffect = 'move';

    const start = startIndex;
    const end = index;

    if (start < end) {
      steps = [...steps.slice(0, start), ...steps.slice(start + 1, end + 1), steps[start], ...steps.slice(end + 1)];
    } else {
      steps = [...steps.slice(0, end), steps[start], ...steps.slice(end, start), ...steps.slice(start + 1)];
    }

    hovering = null;
    startIndex = null;
  };

  const onSave = async () => {
    updating = true;

    // Filter out blank steps (steps without an action)
    const stepsToSave = steps.filter((step) => step.action);

    try {
      if (isEditMode) {
        await updateWorkflow(workflow.name, {
          name: workflowName,
          steps: stepsToSave
        });

        addNotification({
          text: `Workflow "${workflowName}" updated successfully`,
          type: 'success',
          autohide: 3000,
          position: 'top-right'
        });
      } else {
        await createWorkflow({
          name: workflowName,
          steps: stepsToSave
        });

        addNotification({
          text: `Workflow "${workflowName}" created successfully`,
          type: 'success',
          autohide: 3000,
          position: 'top-right'
        });

        navigate(`/staff/workflows/${workflowName}`);
      }

      resetModal();
    } catch (err) {
      error = err.message || `Failed to ${isEditMode ? 'update' : 'create'} workflow`;
      addNotification({
        text: error,
        type: 'danger',
        autohide: 5000,
        position: 'top-right'
      });
    } finally {
      updating = false;
    }
  };

  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      resetModal();
    }
  };

  $: isEditMode = !!workflow;

  $: if (workflow && show && !initialized) {
    workflowName = workflow.name;
    steps =
      workflow.steps?.length > 0
        ? JSON.parse(JSON.stringify(workflow.steps))
        : [
            {
              action: '',
              data: {}
            }
          ];
    initialized = true;
  }

  $: if (!show && initialized) {
    initialized = false;
  }

  $: show === false && resetModal();
  $: stepsWithActions = steps.filter((step) => step.action).length;
  $: hasEnoughSteps = stepsWithActions >= 2;
  $: isDisabled = !workflowName || !!nameError || !hasEnoughSteps;
</script>

<Modal bind:isOpen={show} size="lg">
  <ModalBody class="create-workflow-modal">
    <button class="close" on:click={() => (show = !show)}>
      <Icon name="x" />
    </button>

    <div>
      <div class="title">
        <h1>{isEditMode ? 'Edit Workflow' : 'New Workflow'}</h1>
        <p>Define workflows to streamline operations and processes.</p>
      </div>

      <div class="create-workflow-form">
        <div class="section">
          <h3>Name</h3>
          <Input
            type="text"
            bind:value={workflowName}
            on:keyup={onNameKeyUp}
            disabled={updating}
            placeholder="Enter workflow name"
            invalid={!!nameError}
            feedback={nameError}
          />
        </div>

        <div class="section">
          <h3>Steps <small>(2 minimum)</small></h3>
          <div class="steps-container">
            <div class="steps-list">
              {#each steps as step, index}
                <div
                  class="step-item"
                  class:dragging={hovering === index}
                  draggable={steps.length > 1}
                  on:dragstart={(event) => dragstart(event, index)}
                  on:drop|preventDefault={(event) => drop(event, index)}
                  on:dragover|preventDefault
                  on:dragenter|preventDefault={() => (hovering = index)}
                  role="button"
                  tabindex="0"
                >
                  <Icon name="grip-vertical" class="text-body-tertiary flex-shrink-0" />
                  <div class="step-number">{index + 1}</div>
                  <div class="step-content">
                    <Select
                      items={actionItems}
                      bind:value={step.action}
                      placeholder="Select action..."
                      searchable
                      clearable
                      listMaxHeight={200}
                      itemFilter={actionItemFilter}
                    >
                      <svelte:fragment slot="item" let:item>
                        <div class="action-item">
                          <div class="action-label">{item.label}</div>
                          {#if item.description}
                            <div class="action-description">{item.description}</div>
                          {/if}
                        </div>
                      </svelte:fragment>
                      <div slot="empty" class="empty">
                        {$format('label.NO_OPTIONS_AVAILABLE')}
                      </div>
                    </Select>
                  </div>
                  {#if steps.length > 1}
                    <Icon name="trash" role="button" tabindex="0" class="pointer" on:click={() => removeStep(index)} />
                  {:else}
                    <Icon name="trash" style="opacity: 0.3; cursor: not-allowed;" />
                  {/if}
                </div>
              {/each}
            </div>
            <div class="steps-actions">
              <Button color="secondary" size="sm" on:click={onAddStep}>
                <span class="d-flex align-items-center gap-1">
                  <Icon name="plus" />
                  <span>Step</span>
                </span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ModalBody>
  <ModalFooter>
    <Button color="primary" disabled={isDisabled || updating} on:click={onSave}>
      {isEditMode ? 'Update Workflow' : 'Create Workflow'}
    </Button>
  </ModalFooter>
</Modal>

<svelte:window on:keydown={onKeyDown} />

<style lang="scss" src="./create.scss"></style>
